import { useRef } from "react";
import { GrButton } from "@jnpmedi/grebob";
import { useParams, useRouteMatch } from "react-router-dom";
import { FileDownloadButton } from "@newComponents/common/Viewer/FileDownloadButton";
import { ViewportCenterCircle } from "@newComponents/common/ViewportCenterCircle";
import { InformationGuide } from "@newComponents/layout/InformationGuide";
import { MvnMessage } from "@newLibs/utils";
import { DocumentContextProvider } from "@newHooks/context/document/document.context";
import { DocumentMarkerProvider } from "@newHooks/context/document/documentMarker.context";
import { FileDetailViewerContextProvider } from "@newHooks/context/viewer/fileDetailViewer.context";
import { useFolderFileByIdQuery } from "@newHooks/query/folderFile/folderFileById.query";
import { useMyOrganizationByIdQuery } from "@newHooks/query/myOrganization/myOrganizationById.query";
import { useBigIntParam, useRoute } from "@newHooks/route/useRoute";
import { FileDetailContainer } from "../../newComponents/common/Viewer/FileDetailContainer";
import { COMMON_CODE } from "@maven-msg/code/common";
import { DOCS_FOLDER_CODE } from "@maven-msg/code/docs";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { BrowseEntryType } from "@maven-rest/docs/Folder.dto";
import { GetFolderFileByIdResponse } from "@maven-rest/docs/FolderFile.dto";
import { FolderSharingRole } from "@maven-rest/docs/FolderSharing.dto";
import { isSharingRoleAuthorized } from "@maven-rest/docs/FolderSharing.lib";

export const DOCUMENT_FOLDER_FILE_PDF_REFETCH_DELAY = 3000;
export function ArchiverFileDetailPage() {
  const { pushPath } = useRoute();
  const { url: matchUrl } = useRouteMatch();
  const { fileId: fileIdStr } = useParams<{ fileId: string }>();
  const fileId = useBigIntParam(fileIdStr);

  const isFolderFileFetchEnableRef = useRef<boolean>(true);

  const {
    data: fileData,
    isInitialLoading: isFileInitialLoading,
    error: fileError,
  } = useFolderFileByIdQuery(
    { fileId },
    {
      enabled: !!fileId && isFolderFileFetchEnableRef.current,
      refetchInterval: DOCUMENT_FOLDER_FILE_PDF_REFETCH_DELAY,
      onSuccess: (data) => {
        if ("document" in data.file && "docsPdfId" in data.file.document) {
          isFolderFileFetchEnableRef.current = String(data.file.document.docsPdfId) === "null";
        }
      },
    }
  );

  const { error: organizationError } = useMyOrganizationByIdQuery(
    { organizationId: fileData?.file.resource.organizationId ?? BigInt(-1) },
    { enabled: !!fileData?.file.resource.organizationId }
  );

  if (isFileInitialLoading) {
    return <ViewportCenterCircle />;
  }

  if (fileError || organizationError) {
    return (
      <InformationGuide
        style={{ height: "100%", backgroundColor: "inherit" }}
        title={MvnMessage(COMMON_CODE.UNHANDLED_EXCEPTION_WITH_MSG, { message: fileError?.responseMessage || "Unknown" }, [
          DOCS_FOLDER_CODE.DOCS_FOLDER_FILE_ACCESS_DENIED_IP_WHITELIST,
        ])}
        emptyType="noFile"
        description=""
        confirmButton={
          <GrButton onClick={() => pushPath(matchUrl.split("/files/")[0] || ".")}>{MvnMessage(COMMON_LANG.BACK_TO_LIST)}</GrButton>
        }
      />
    );
  }

  return (
    <>
      {fileData?.file.type === BrowseEntryType.Document ? (
        <DocumentContextProvider
          key={getDocumentDetailViewerKey(fileData)}
          mode="VIEWER"
          programmatically={false}
          documentFile={fileData.file}
        >
          <DocumentMarkerProvider markers={[]}>
            <FileDetailViewerContextProvider>
              <FileDetailContainer
                header={{
                  left: (
                    <GrButton
                      type="button"
                      color="grey"
                      variant="outlined"
                      startIcon="arrow-narrow-left"
                      startIconSize={12}
                      onClick={() => pushPath(matchUrl.split("/files/")[0] || ".")}
                    >
                      {MvnMessage(COMMON_LANG.BACK_TO_LIST)}
                    </GrButton>
                  ),
                  right: (
                    <>
                      {isSharingRoleAuthorized({
                        myRole: fileData.file.role,
                        role: FolderSharingRole.Viewer,
                        exceptRole: [FolderSharingRole.OnlineContributor],
                      }) && <FileDownloadButton fileId={fileData.file.resource.id} />}
                    </>
                  ),
                }}
                onFolderFileByIdApiEnable={() => (isFolderFileFetchEnableRef.current = true)}
              />
            </FileDetailViewerContextProvider>
          </DocumentMarkerProvider>
        </DocumentContextProvider>
      ) : (
        <InformationGuide
          style={{ height: "100%", backgroundColor: "inherit" }}
          title={"문서 형식이 올바르지 않습니다."}
          description={"관리자에게 문의하세요"}
        />
      )}
    </>
  );
}

export function getDocumentDetailViewerKey(documentFile?: GetFolderFileByIdResponse) {
  if (!documentFile) return undefined;

  if ("document" in documentFile.file && "docsPdfId" in documentFile.file.document) {
    return String(documentFile.file.document.docsPdfId);
  }

  return undefined;
}
